import PropTypes from 'prop-types';
import { addWeeks, isBefore, differenceInMilliseconds } from 'date-fns';
import {
    SENDING_DOMAIN_STATES,
    DOMAIN_WARMING_VISIBILITY_WEEKS,
    DOMAIN_WARMING_TOTAL_WEEKS,
} from '../../utils/constants';

/**
 * @typedef {import('../../utils/constants').SendingDomainState} SendingDomainState
 */

/**
 * Statuses that indicate domain warming should be shown
 * @type {string[]}
 */
const DOMAIN_WARMING_VISIBLE_STATUSES = [
    SENDING_DOMAIN_STATES.PENDING,
    SENDING_DOMAIN_STATES.VERIFIED,
];

const VERIFIED_TITLE = 'Your domain is warming up';
const VERIFIED_TEXT = `To ensure the best deliverability possible, we're slowly increasing the volume of emails
                sent from your new domain to build your sender reputation — helping your emails land in
                the inbox, not spam. The warm-up process happens automatically in the background, and
                typically takes about 6 weeks, depending on your list size and how often you send.`;

const PENDING_TITLE = 'Domain will start warming up soon';
const PENDING_TEXT = `As soon as your sending domain is activated, we'll slowly increase the volume of emails
                sent from your new domain to build your sender reputation — helping your emails land in
                the inbox, not spam. The warm-up process happens automatically in the background, and
                typically takes about 6 weeks, depending on your list size and how often you send.`;

const COMPLETED_TITLE = 'Domain warming complete';
const COMPLETED_TEXT = `Your domain warming is complete. All newsletters are now being sent from your new domain.`;

/**
 * URL to the Ghost help docs for domain warming
 * @type {string}
 */
const DOMAIN_WARMING_LEARN_MORE_URL = 'https://ghost.org/help/custom-sending-domains/';

/**
 * Check if the domain warming note should be visible based on the sending domain status updated date
 * @param {string|null} sendingDomainUpdatedAt - ISO date string of when the sending domain status was last updated
 * @returns {boolean} True if the note should be visible (within the visibility window)
 */
function isWithinVisibilityWindow(sendingDomainUpdatedAt) {
    if (!sendingDomainUpdatedAt) {
        return false;
    }

    const updatedAt = new Date(sendingDomainUpdatedAt);
    const deadline = addWeeks(updatedAt, DOMAIN_WARMING_VISIBILITY_WEEKS);
    const now = new Date();

    return isBefore(now, deadline);
}

/**
 * Calculate the domain warming progress percentage based on elapsed time
 * @param {string|null} sendingDomainUpdatedAt - ISO date string of when the domain was verified
 * @returns {number} Progress percentage from 0 to 100
 */
export function calculateWarmingProgress(sendingDomainUpdatedAt) {
    if (!sendingDomainUpdatedAt) {
        return 0;
    }

    const startDate = new Date(sendingDomainUpdatedAt);
    const endDate = addWeeks(startDate, DOMAIN_WARMING_TOTAL_WEEKS);
    const now = new Date();

    const totalDuration = differenceInMilliseconds(endDate, startDate);
    const elapsedDuration = differenceInMilliseconds(now, startDate);

    const progress = Math.round((elapsedDuration / totalDuration) * 100);

    return Math.min(Math.max(progress, 0), 100);
}

/**
 * Get the title text based on verification and completion status
 * @param {boolean} isVerified
 * @param {boolean} isCompleted
 * @returns {{title: string, text: string}}
 */
const getTitleAndText = (isVerified, isCompleted) => {
    if (isCompleted) {
        return {
            title: COMPLETED_TITLE,
            text: COMPLETED_TEXT,
        };
    }

    if (isVerified) {
        return {
            title: VERIFIED_TITLE,
            text: VERIFIED_TEXT,
        };
    }

    return {
        title: PENDING_TITLE,
        text: PENDING_TEXT,
    };
};

/**
 * DomainWarmingNote displays an informational note about domain warming
 * when a custom sending domain is being set up or has been verified.
 *
 * @param {object} props
 * @param {boolean} props.domainWarming - Whether domain warming is active for this site
 * @param {SendingDomainState} props.sendingDomainStatus - Current status of the sending domain
 * @param {string|null} props.sendingDomainUpdatedAt - ISO date string of when the sending domain status was last updated
 */
export default function DomainWarmingNote({
    domainWarming,
    sendingDomainStatus,
    sendingDomainUpdatedAt,
}) {
    const hasValidStatus = DOMAIN_WARMING_VISIBLE_STATUSES.includes(sendingDomainStatus);
    const isWithinWindow = isWithinVisibilityWindow(sendingDomainUpdatedAt);
    const shouldShow = domainWarming && hasValidStatus && isWithinWindow;

    if (!shouldShow) {
        return null;
    }

    const isVerified = sendingDomainStatus === SENDING_DOMAIN_STATES.VERIFIED;
    const progress = calculateWarmingProgress(sendingDomainUpdatedAt);
    const isCompleted = progress >= 100;
    const { title, text } = getTitleAndText(isVerified, isCompleted);

    return (
        <div className="domain-warming container" data-testid="domain-warming-note">
            <div className={`progress-section ${isVerified ? 'verified' : ''}`}>
                <div className="progress-header">
                    <h4 className="progress-title">{title}</h4>
                    {isVerified && <span className="progress-percentage">{progress}%</span>}
                </div>

                {isVerified && (
                    <div className="progress-track" data-testid="domain-warming-progress">
                        <div
                            className="progress-bar-clip"
                            style={{ width: `${progress}%` }}
                            data-testid="domain-warming-progress-bar-clip"
                        >
                            <div
                                className="progress-bar"
                                style={{
                                    width: progress > 0 ? `${(100 / progress) * 100}%` : '100%',
                                }}
                                data-testid="domain-warming-progress-bar"
                            />
                        </div>
                    </div>
                )}
            </div>

            <p>
                {text}{' '}
                <a href={DOMAIN_WARMING_LEARN_MORE_URL} target="_blank" rel="noopener noreferrer">
                    Learn more →
                </a>
            </p>
        </div>
    );
}

DomainWarmingNote.propTypes = {
    domainWarming: PropTypes.bool,
    sendingDomainStatus: PropTypes.string,
    sendingDomainUpdatedAt: PropTypes.string,
};

DomainWarmingNote.defaultProps = {
    domainWarming: false,
    sendingDomainStatus: null,
    sendingDomainUpdatedAt: null,
};
